import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useRef, useMemo } from 'react';
import { Item } from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import styles from './Menu2.js';
import { usePaneContext, useItemContext } from './MenuContext.js';
import '../../utils/index.js';
import { DATA_TESTID_SUFFIXES } from '../Select/constants.js';
import { DATA_TESTID_SUFFIXES as DATA_TESTID_SUFFIXES$1, KEYS } from './constants.js';
import useSelectContext from '../Select/SelectContext.js';
import { conditionalA11yHelpersWrapper, useIsFocusVisible } from '../../components/UIProvider/A11yHelpersContext.js';
import getSubDataTestId from '../../utils/getSubDataTestId/getSubDataTestId.js';
import mergeRefs from '../../utils/mergeRefs/mergeRefs.js';

const MenuItem = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  let {
    isDisabled,
    asChild,
    children,
    className,
    onSelect,
    label,
    ...rest
  } = _ref;
  const selectContext = useSelectContext(true);
  const withinSelect = selectContext !== null;
  const multiselect = selectContext === null || selectContext === void 0 ? void 0 : selectContext.multiselect;
  const itemRef = useRef(null);
  const {
    dataTestId: menuParentDataTestId,
    childIndex,
    isInteractive,
    closeSubmenu,
    flyoutNode,
    stickyBoxRef,
    focusVirtualBoundary,
    isVirtualized: paneIsVirtualized,
    maskIsReady
  } = usePaneContext(itemRef);
  const {
    virtualIndex
  } = useItemContext();
  const index = useMemo(() => virtualIndex !== null && virtualIndex !== void 0 ? virtualIndex : childIndex, [childIndex, virtualIndex]);
  const {
    isFocusVisible
  } = useIsFocusVisible(true);
  const dataTestId = getSubDataTestId(menuParentDataTestId === null || menuParentDataTestId === void 0 ? void 0 : menuParentDataTestId.replace(withinSelect ? DATA_TESTID_SUFFIXES.SELECT_PANE : DATA_TESTID_SUFFIXES$1.MENU_PANE, withinSelect ? DATA_TESTID_SUFFIXES.SELECT_ITEM : DATA_TESTID_SUFFIXES$1.MENU_ITEM), String(index));
  const handleFocus = () => {
    if (itemRef.current && flyoutNode && isFocusVisible) {
      var _stickyBoxRef$current, _stickyBoxRef$current2;
      const stickyBoxHeight = (_stickyBoxRef$current = (_stickyBoxRef$current2 = stickyBoxRef.current) === null || _stickyBoxRef$current2 === void 0 ? void 0 : _stickyBoxRef$current2.clientHeight) !== null && _stickyBoxRef$current !== void 0 ? _stickyBoxRef$current : 0;

      // if the top of the focused menu item is beneath the sticky box
      if (itemRef.current.offsetTop < flyoutNode.scrollTop + stickyBoxHeight) {
        // scroll down a little
        flyoutNode.scrollTop = itemRef.current.offsetTop - stickyBoxHeight;
      }
    }
  };
  return /*#__PURE__*/React__default.createElement(Item, _extends({}, rest, {
    tabIndex: 0,
    asChild: asChild,
    "aria-disabled": isDisabled,
    className: classNames(className, styles.menuItem, {
      [styles.unresponsive]: !isInteractive
    }),
    "data-testid": dataTestId,
    ref: mergeRefs(ref, itemRef),
    onKeyDown: e => {
      if (!isInteractive || multiselect && e.key === 'Enter') {
        e.preventDefault();
        return;
      }
      if (KEYS.CLOSE_SUBMENU.includes(e.key)) {
        closeSubmenu === null || closeSubmenu === void 0 || closeSubmenu(e);
      } else if (paneIsVirtualized && KEYS.NAVIGATE_TOP.includes(e.key)) {
        e.preventDefault();
        focusVirtualBoundary('top');
      } else if (paneIsVirtualized && KEYS.NAVIIGATE_BOTTOM.includes(e.key)) {
        e.preventDefault();
        focusVirtualBoundary('bottom');
      }
    },
    onFocus: handleFocus
    // stop radix from closing the menu on selection of disabled item
    // other pointer and keyboard events still work, even when disabled
    ,
    onSelect: e => {
      if (isDisabled || maskIsReady === false) {
        e.preventDefault();
        return;
      }
      if (multiselect) {
        e.preventDefault();
      }
      onSelect === null || onSelect === void 0 || onSelect();
    }
  }), (!children || typeof children === 'string') && label ? label : children);
});
var MenuItem$1 = conditionalA11yHelpersWrapper(MenuItem, 'textInput');

export { MenuItem$1 as default };
